import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
class LogoAnimation {
    constructor() {
        gsap.registerPlugin(ScrollTrigger);
        this.logo = document.querySelector(
            ".nav.logo-animation .nav__logo svg"
        );
        this.lockupdiv = document.querySelector(".home-lockup");
        this.lockup = document.querySelector(
            ".home-lockup .home-lockup__intro"
        );
        this.layout = document.querySelector(".layout");
        this.nav = document.querySelector(".nav");
        this.viewportHeight = window.innerHeight;
        this.layoutMargin = parseInt(
            window.getComputedStyle(this.layout).marginBottom
        );
        this.currentOrientation =
            window.innerWidth > window.innerHeight ? "landscape" : "portrait";

        this.breakPointDims = {
            320: {
                scale: 0.3477,
                translateY: this.calcTy(window.innerWidth) + "px",
                lockupDiv: 150,
                yPercent: this.calcYPercent(window.innerWidth),
            },
            480: {
                scale: 0.3477,
                translateY: "-333px",
                lockupDiv: 100,
                yPercent: 30,
            },
            800: {
                scale: 0.25,
                translateY: "-594px",
                lockupDiv: 70,
                yPercent: 20,
            },
            1030: {
                scale: 0.24,
                translateY: "-516px",
                lockupDiv: 165,
                yPercent: 47,
            },
            1200: {
                scale: 0.2827,
                translateY: "-575px",
                lockupDiv: 180,
                yPercent: 47,
            },
            1440: {
                scale: 0.2353,
                translateY: "-595px",
                lockupDiv: 180,
                yPercent: 47,
            },
        };

        this.resize();
        this.orientationChange();
        this.selectWidth();
    }

    resize() {
        window.addEventListener("resize", () => {
            const newOrientation =
                window.innerWidth > window.innerHeight
                    ? "landscape"
                    : "portrait";

            // Check if orientation actually changed
            if (newOrientation !== this.currentOrientation) {
                this.currentOrientation = newOrientation;

                // Force complete reset on orientation change
                this.forceReset();
            } else if (this.viewportHeight !== window.innerHeight) {
                this.viewportHeight = window.innerHeight;
            } else {
                this.selectWidth();
            }
        });
    }

    orientationChange() {
        // Listen for orientation change events (iOS Safari)
        window.addEventListener("orientationchange", () => {
            setTimeout(() => {
                const newOrientation =
                    window.innerWidth > window.innerHeight
                        ? "landscape"
                        : "portrait";
                if (newOrientation !== this.currentOrientation) {
                    this.currentOrientation = newOrientation;
                    this.forceReset();
                }
            }, 100); // Small delay to allow viewport to settle
        });
    }

    forceReset() {
        // Kill all animations and ScrollTriggers immediately
        if (this.logoAnim) {
            this.logoAnim.scrollTrigger && this.logoAnim.scrollTrigger.kill();
            this.logoAnim.kill();
            this.logoAnim = null;
        }
        if (this.navAnim) {
            this.navAnim.scrollTrigger && this.navAnim.scrollTrigger.kill();
            this.navAnim.kill();
            this.navAnim = null;
        }
        if (this.lockupAnim) {
            this.lockupAnim.scrollTrigger &&
                this.lockupAnim.scrollTrigger.kill();
            this.lockupAnim.kill();
            this.lockupAnim = null;
        }
        if (this.mainAnim) {
            this.mainAnim.scrollTrigger && this.mainAnim.scrollTrigger.kill();
            this.mainAnim.kill();
            this.mainAnim = null;
        }

        // Completely clear all transforms and reset to natural state
        gsap.set([this.logo, this.lockup, this.nav, "main.home"], {
            clearProps: "all",
            transform: "none",
        });

        // Reset layout styles
        this.layout.style.marginBottom = "";

        // Force ScrollTrigger refresh after a brief delay to allow DOM to settle
        setTimeout(() => {
            ScrollTrigger.refresh();
            this.selectWidth();
        }, 100);
    }

    selectWidth() {
        const bpArray = Object.keys(this.breakPointDims);
        let dims;
        let selectedBreakpoint = null;
        bpArray.forEach((value) => {
            const breakpoint = this.breakPointDims[value];
            // Handle landscape breakpoints with custom queries
            if (breakpoint.query) {
                this.mediaQuery = window.matchMedia(breakpoint.query);
            } else {
                // Handle numeric breakpoints (legacy)
                this.mediaQuery = window.matchMedia(`(min-width: ${value}px)`);
            }
            if (this.mediaQuery.matches) {
                dims = breakpoint;
                selectedBreakpoint = value;
            }
        });

        // Fallback to smallest breakpoint if no match found
        if (!dims) {
            dims = this.breakPointDims[320];
            selectedBreakpoint = "320 (fallback)";
        }

        // Kill existing animations and their ScrollTriggers
        if (this.logoAnim) {
            this.logoAnim.scrollTrigger && this.logoAnim.scrollTrigger.kill();
            this.logoAnim.kill();
        }
        if (this.navAnim) {
            this.navAnim.scrollTrigger && this.navAnim.scrollTrigger.kill();
            this.navAnim.kill();
        }
        if (this.lockupAnim) {
            this.lockupAnim.scrollTrigger &&
                this.lockupAnim.scrollTrigger.kill();
            this.lockupAnim.kill();
        }
        if (this.mainAnim) {
            this.mainAnim.scrollTrigger && this.mainAnim.scrollTrigger.kill();
            this.mainAnim.kill();
        }

        // Clear all GSAP transforms to reset elements to natural state
        gsap.set([this.logo, this.lockup, this.nav, "main.home"], {
            clearProps: "all",
        });

        // Refresh ScrollTrigger to recalculate positions
        ScrollTrigger.refresh();

        // Handle lockupDiv properties
        if (dims.lockupDiv) {
            this.layout.style.marginBottom = `${
                this.layoutMargin - dims.lockupDiv
            }px`;
        }

        this.animate(dims);
    }

    animate(dims) {
        // logo animation
        this.logoAnim = gsap.to(this.logo, {
            scrollTrigger: {
                trigger: this.lockupdiv,
                start: "top 24px",
                end: "+380px",
                scrub: true,
            },
            scale: dims.scale,
            translateX: 0,
            translateY: dims.translateY,
            reversed: true,
            ease: "power1.out",
        });

        // main video banner position
        this.mainAnim = gsap.to("main.home", {
            scrollTrigger: {
                trigger: this.lockupdiv,
                start: "top top",
                end: "+440px -40px",
                scrub: true,
            },
            y: `${dims.lockupDiv || 100}px`,
        });

        // lockup animation
        this.lockupAnim = gsap.to(this.lockup, {
            scrollTrigger: {
                trigger: this.lockupdiv,
                start: "top top",
                end: "+440px -40px",
                scrub: true,
            },
            y: `${dims.lockupDiv || 100}px`,
        });

        // Nav animation
        this.navAnim = gsap.to(this.nav, {
            scrollTrigger: {
                trigger: this.lockupdiv,
                start: "+340px",
                end: "+440px -120px",
                scrub: true,
            },
            translateY: "-100px",
        });
    }

    calcTy(screenWidth) {
        // Weird calculation but it seems to work
        // Based on font size calculation and trial & error to get the right value
        // return (150 + 230 * (screenWidth - 320) / 576) - 327;
        return -180 + (0 * (screenWidth - 320)) / 576;
    }

    calcYPercent(screenWidth) {
        return 55 + (0 * (screenWidth - 320)) / 576;
    }
}

export default LogoAnimation;
